import { Call } from '@twilio/voice-sdk';

import {
  CallInfoAggregated,
  CallWithDuration,
  GetCustomerAPIDataType,
  CallDetailResponse,
} from '../types';

type PopulatedSource = 'twilio' | 'callDetail' | 'customer';

/**
 * Extracts the "other party" phone number from an active Twilio call.
 * For inbound calls: the caller's number (FromClient)
 * For outbound calls: the destination number (To)
 */
const getPhoneNumberFromActiveCall = (call: Call): string => {
  const fromClient = call.customParameters.get('FromClient');
  if (fromClient) {
    return fromClient;
  }
  return call.customParameters.get('To') || '';
};

/**
 * Determines direction from a Twilio Call object.
 * Check for 'out' first since 'outgoing' contains 'in' as a substring.
 */
const getDirectionFromActiveCall = (call: Call): 'inbound' | 'outbound' => {
  const direction = String(call.direction).toLowerCase();
  if (direction.includes('out')) {
    return 'outbound';
  }
  return 'inbound';
};

/**
 * Class-based builder for creating CallInfoAggregated from various data sources.
 * Implements CallInfoAggregated so it can be passed directly to CallInfo component.
 * Returns new instances from enrichment methods (immutable pattern for React state).
 */
export class CallInfoBuilderClass implements CallInfoAggregated {
  direction: 'inbound' | 'outbound' = 'outbound';
  status: string = 'loading';
  phoneNumber: string = '';
  isActive: boolean = false;

  id?: number;
  externalKey?: string;
  statusCode?: string;
  createdAt?: string;
  duration?: number;
  contactName?: string;
  customer?: {
    id: number;
    fullName: string;
    primaryPhone?: string;
    primaryEmail?: string;
  };
  order?: {
    id: number;
  };
  hasWarmTransfer?: boolean;
  warmTransferAgent?: string;
  fromNumber?: {
    callerId: string;
    lineName?: string;
  };
  isMuted?: boolean;
  isOnHold?: boolean;

  private _populatedSources: Set<PopulatedSource> = new Set();

  /**
   * Private constructor - use factory methods instead.
   */
  private constructor(init?: Partial<CallInfoBuilderClass>) {
    if (init) {
      Object.assign(this, init);
      if (init._populatedSources) {
        this._populatedSources = new Set(init._populatedSources);
      }
    }
  }

  /**
   * Check if we have data from any source (enough to render).
   */
  hasData(): boolean {
    return this._populatedSources.size > 0;
  }

  /**
   * Check if a specific source has populated data.
   */
  hasSource(source: PopulatedSource): boolean {
    return this._populatedSources.has(source);
  }

  /**
   * Get all populated sources.
   */
  getPopulatedSources(): PopulatedSource[] {
    return Array.from(this._populatedSources);
  }

  /**
   * Create a new instance with Twilio call data.
   */
  withTwilioCall(
    call: CallWithDuration,
    status: string,
    duration: number,
    isMuted?: boolean,
    isOnHold?: boolean,
  ): CallInfoBuilderClass {
    const newInstance = this._clone();
    const direction = getDirectionFromActiveCall(call);
    const phoneNumber = getPhoneNumberFromActiveCall(call);

    newInstance.externalKey = call.parameters?.CallSid;
    newInstance.id = call.dbCallId;
    newInstance.direction = direction;
    newInstance.status = status;
    newInstance.phoneNumber = phoneNumber;
    newInstance.isActive = true;
    newInstance.duration = duration;
    newInstance.isMuted = isMuted;
    newInstance.isOnHold = isOnHold;

    // Add created at from durationInitial if available
    if (call.durationInitial) {
      newInstance.createdAt = new Date(call.durationInitial).toISOString();
    }

    // Check for warm transfer
    const isWarmTransfer = call.customParameters.get('warm_transfer') === 'True';
    const invitingAgent = call.customParameters.get('inviting_agent');
    if (isWarmTransfer && invitingAgent) {
      newInstance.hasWarmTransfer = true;
      newInstance.warmTransferAgent = invitingAgent;
    }

    // Add caller ID info for outbound calls
    if (direction === 'outbound' && call.usedCallerId) {
      const lineName = call.usedLineName?.replace(/^Call - /, '');
      newInstance.fromNumber = {
        callerId: call.usedCallerId,
        lineName,
      };
    }

    newInstance._populatedSources.add('twilio');
    return newInstance;
  }

  /**
   * Find a matching active call from the list and enrich with its data.
   * Matches by callId (database ID) or sid (Twilio CallSid).
   * Returns unchanged instance if no match found.
   */
  withActiveCallMatch(
    calls: CallWithDuration[],
    options: {
      callId?: number;
      sid?: string;
      status: string;
      duration: number;
      isMuted?: boolean;
      isOnHold?: boolean;
    },
  ): CallInfoBuilderClass {
    const { callId, sid, status, duration, isMuted, isOnHold } = options;

    // Find matching call by callId first, then by sid
    let matchingCall: CallWithDuration | undefined;

    if (callId) {
      matchingCall = calls.find(call => call.dbCallId === callId);
    }

    if (!matchingCall && sid) {
      matchingCall = calls.find(call => call.parameters?.CallSid === sid);
    }

    if (!matchingCall) {
      // No active call match - return instance without twilio source
      const newInstance = this._clone();
      newInstance.isActive = false;
      newInstance._populatedSources.delete('twilio');
      return newInstance;
    }

    return this.withTwilioCall(matchingCall, status, duration, isMuted, isOnHold);
  }

  /**
   * Create a new instance with call detail API response data.
   * This is the primary data source for historical calls.
   */
  withCallDetailResponse(response: CallDetailResponse): CallInfoBuilderClass {
    const newInstance = this._clone();
    const direction = response.direction;
    const phoneNumber =
      direction === 'inbound' ? response.from_phone_number : response.to_phone_number;

    // Get contact info based on direction
    const contactInfo =
      direction === 'inbound' ? response.from_contact_info : response.to_contact_info;

    // Parse duration string to seconds (format: "HH:MM:SS" or "MM:SS")
    let durationSeconds: number | undefined;
    if (response.duration) {
      const parts = response.duration.split(':').map(Number);
      if (parts.length === 3) {
        durationSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
      } else if (parts.length === 2) {
        durationSeconds = parts[0] * 60 + parts[1];
      }
    }

    newInstance.id = response.id;
    newInstance.externalKey = response.external_key;
    newInstance.direction = direction;
    newInstance.status = response.status_label || 'Unknown';
    newInstance.statusCode = response.status_code || undefined;
    newInstance.phoneNumber = phoneNumber;
    newInstance.createdAt = response.created_at;

    // Only update duration if not already set from Twilio (active call)
    if (!newInstance.hasSource('twilio')) {
      newInstance.duration = durationSeconds;
      newInstance.isActive = false;
    }

    // Add customer info if the contact is a customer
    if (contactInfo?.is_customer) {
      newInstance.contactName = contactInfo.full_name;
      newInstance.customer = {
        id: contactInfo.id,
        fullName: contactInfo.full_name,
        primaryPhone: contactInfo.primary_phone?.identifier,
        primaryEmail: contactInfo.primary_email?.identifier,
      };
    } else if (contactInfo) {
      newInstance.contactName = contactInfo.full_name;
    }

    newInstance._populatedSources.add('callDetail');
    return newInstance;
  }

  /**
   * Create a new instance enriched with customer data.
   */
  withCustomerData(customerData: GetCustomerAPIDataType | null): CallInfoBuilderClass {
    if (!customerData) {
      return this;
    }

    const newInstance = this._clone();
    const customer = customerData.customer;
    const orders = customerData.orders || [];

    if (customer) {
      newInstance.customer = {
        id: customer.id,
        fullName: customer.full_name,
        primaryPhone: customer.primary_phone?.identifier,
        primaryEmail: customer.primary_email?.identifier,
      };

      // Use customer name as contact name if not already set
      if (!newInstance.contactName) {
        newInstance.contactName = customer.full_name;
      }
    }

    if (orders.length > 0) {
      newInstance.order = {
        id: orders[0].id,
      };
    }

    newInstance._populatedSources.add('customer');
    return newInstance;
  }

  /**
   * Create a new instance with the call ID set (used when polling finds the call).
   */
  withCallId(callId: number): CallInfoBuilderClass {
    const newInstance = this._clone();
    newInstance.id = callId;
    return newInstance;
  }

  /**
   * Clone the current instance.
   */
  private _clone(): CallInfoBuilderClass {
    const clone = new CallInfoBuilderClass({
      direction: this.direction,
      status: this.status,
      phoneNumber: this.phoneNumber,
      isActive: this.isActive,
      id: this.id,
      externalKey: this.externalKey,
      statusCode: this.statusCode,
      createdAt: this.createdAt,
      duration: this.duration,
      contactName: this.contactName,
      customer: this.customer ? { ...this.customer } : undefined,
      order: this.order ? { ...this.order } : undefined,
      hasWarmTransfer: this.hasWarmTransfer,
      warmTransferAgent: this.warmTransferAgent,
      fromNumber: this.fromNumber ? { ...this.fromNumber } : undefined,
      isMuted: this.isMuted,
      isOnHold: this.isOnHold,
    });
    clone._populatedSources = new Set(this._populatedSources);
    return clone;
  }

  /**
   * Factory method to create an empty builder instance.
   */
  static create(): CallInfoBuilderClass {
    return new CallInfoBuilderClass();
  }

  /**
   * Factory method to create a builder from an active Twilio call.
   */
  static fromTwilioCall(
    call: CallWithDuration,
    status: string,
    duration: number,
    isMuted?: boolean,
    isOnHold?: boolean,
  ): CallInfoBuilderClass {
    return CallInfoBuilderClass.create().withTwilioCall(call, status, duration, isMuted, isOnHold);
  }

  /**
   * Factory method to create a builder from a call detail API response.
   */
  static fromCallDetailResponse(response: CallDetailResponse): CallInfoBuilderClass {
    return CallInfoBuilderClass.create().withCallDetailResponse(response);
  }
}
